import {
	type CommandConstructor,
	type CommandProperties,
	type SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

import type { ConfluenceAccumulatedDataType } from './confluence-accumulated-data-type';

/** Semantic metadata for a generated visual in a completed CWR page-create document. */
export type GeneratedVisualMetadata = {
	resultId: string;
	dynamicUiType: string;
	dynamicUiSubtype?: string;
};

/**
 * Data payload for creating a Confluence page.
 */
export type ConfluenceCreatePageData = ConfluenceAccumulatedDataType & {
	/**
	 * The title to be included in the created page.
	 */
	title: string;
	/**
	 * The adf to be included in the created page.
	 */
	adf: string;
	/**
	 * Set when the backend has deferred generated visuals (images, MAUI apps) to this
	 * (terminal) payload, so its `adf` is the complete document rather than a delta.
	 */
	applyAdf?: boolean;
	/** Maps final-document visual ids to their semantic type and optional subtype. */
	generatedVisualMetadata?: GeneratedVisualMetadata[];
};

/** Creation experiences that represent a whole-page Create with Rovo flow. */
export type CwrCreateExperience = 'cwr' | 'cwr_type';

export const isCwrCreateExperience = (experience?: string): experience is CwrCreateExperience =>
	experience === 'cwr' || experience === 'cwr_type';

/**
 * Properties for the Confluence page creation command.
 *
 * @extends CommandProperties
 */
export interface ConfluenceCreatePageCommandProperties extends CommandProperties {
	/**
	 * The unique key identifier for the page creation request.
	 */
	key: string;
	/**
	 * The data payload containing the content for the new page.
	 */
	data: ConfluenceCreatePageData;
	/**
	 * Whether the stream is complete.
	 */
	isStreamComplete: boolean;
	/**
	 * Trace id of the request
	 */
	traceId?: string;
	/**
	 * Request id for E2E event tracking and request correlation
	 */
	requestId?: string;
	/**
	 * Single instrumentation id of the request
	 * This is used to align events between rovo and the page command that may have
	 * actioned / dismissed the command
	 */
	singleInstrumentationId: string | undefined;
	/**
	 * Rovo chat entry point that initiated the creation (e.g. `cwrBlankObject`),
	 * so the receiving editor can attribute its analytics to the originating flow.
	 */
	entryPoint?: string;
	/**
	 * Normalized creation experience used to distinguish CWR page creation from
	 * other flows that use the same page-create command.
	 */
	cwrCreateExperience?: CwrCreateExperience;
	/**
	 * Rovo chat conversation id, so the receiving editor's analytics can join back to the
	 * conversation's unified AI events.
	 */
	conversationChannelId?: string;
}
/**
 * A command sent by a bridge API client to create a new Confluence page.
 *
 * @summary {@link CommandProperties.senderId} is requesting {@link CommandProperties.receiverId} to create a Confluence page with the specified content.
 */
export const ConfluencePageCreateCommand: CommandConstructor<
	ConfluenceCreatePageCommandProperties,
	SerializableValue
> = createCommand<ConfluenceCreatePageCommandProperties>('confluence-page-create-command');
